import {
  AppDialog,
  AppDialogHeader,
  type DialogCloseReason,
} from "@dfp/react-components";
import { UserCancelledRequestError } from "@dfp/react-legacy-async-components";

import { RequestAuthorization } from "../types/requestAuthorization.ts";
import { useTT } from "../useTT.ts";
import { CodeInputAuthDialog } from "./CodeInputAuthDialog.tsx";
import { MobileKeyAuthDialog } from "./MobileKeyAuthDialog.tsx";
import {
  REMOVE_MOBILE_KEY_AUTHORIZATION_DIALOG_NAME,
  RemoveMobileKeyAuthorizationDialog,
} from "./RemoveMobileKeyAuthorizationDialog.tsx";

type Props = RequestAuthorization;

export function AuthorizationDialog(props: Props) {
  const { tt } = useTT();

  const {
    onAuthError,
    errorAttributes: { authMethod, authPersonName },
  } = props;
  const inputData = props.inputData as object;

  const handleClickClose = (
    event: React.MouseEvent<HTMLButtonElement>,
    reason: DialogCloseReason = "iconClick",
  ) => {
    // ignore backdrop click (that might be accidental)
    // but allow escape key (that seems pretty intentional)
    if (reason === "backdropClick") {
      return;
    }
    onAuthError(new UserCancelledRequestError());
  };

  const isRemoveRegisteredDeviceDialog =
    authMethod === "Email" &&
    "reason" in inputData &&
    typeof inputData.reason === "string" &&
    inputData.reason.startsWith(REMOVE_MOBILE_KEY_AUTHORIZATION_DIALOG_NAME);
  const reasonSplit =
    ("reason" in inputData &&
      typeof inputData.reason === "string" &&
      inputData.reason.split("|")) ||
    [];

  const authTitle = () => {
    if (isRemoveRegisteredDeviceDialog) {
      return tt("dialog.RemoveMobileKeyDialog.title", {
        device: reasonSplit?.[2] ?? "",
      });
    } else if (authPersonName) {
      return tt("dialog.OnBehalfOf.title", { name: authPersonName });
    } else {
      return tt("dialog.Generic.title");
    }
  };

  return (
    <AppDialog
      open
      onClose={handleClickClose}
      // see https://github.com/mui/material-ui/issues/33004 why just <Input autoFocus /> is not enough
      disableRestoreFocus
    >
      <AppDialogHeader>{authTitle()}</AppDialogHeader>
      <AuthContent
        props={props}
        onClickClose={handleClickClose}
        isRemoveRegisteredDeviceDialog={isRemoveRegisteredDeviceDialog}
      />
    </AppDialog>
  );
}

type AuthContentProps = {
  props: Props;
  onClickClose: (event: React.MouseEvent<HTMLButtonElement>) => void;
  isRemoveRegisteredDeviceDialog: boolean;
};

function AuthContent({
  props,
  onClickClose,
  isRemoveRegisteredDeviceDialog,
}: AuthContentProps) {
  const { authMethod } = props.errorAttributes;
  if (isRemoveRegisteredDeviceDialog) {
    return <RemoveMobileKeyAuthorizationDialog {...props} />;
  } else if (authMethod === "MobileKey") {
    return <MobileKeyAuthDialog onClickClose={onClickClose} {...props} />;
  } else {
    return <CodeInputAuthDialog {...props} />;
  }
}
